/**
 * #MT-B2FA396 — Fase 3B: UI gerencial Agendamento de Docas de Entrada.
 * Menu Movimentação → Agendamento de Docas (#input-dock-schedules).
 */
import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import { notify, ask } from '../../versa/msgbox.js';
import pdf from '../../versa/pdf.js';
import view from '../views/input-dock-schedules.js';
import detailForm from '../forms/input-dock-schedule-detail.js';
import cancelForm from '../forms/input-dock-schedule-cancel.js';
import * as types from '../types.js';
import opt_input_dock_type from '../types/opt_input_dock_type.js';
import opt_input_dock_schedule_status from '../types/opt_input_dock_schedule_status.js';
import { resolveCompanyId, resolveWarehouseId } from '../utils/location-search.js';
import {
	openScheduleEditor as openSharedScheduleEditor,
	showScheduleDetail,
} from '../utils/input-dock-schedule-ui.js';
import { mountDockScheduleCalendar } from '../utils/input-dock-schedule-calendar.js';
import {
	statusLabel,
	dockTypeLabel,
	eventLabel,
	formatDate,
	formatTime,
	canMutateStatus,
	MSG_FLAG_OFF,
} from '../utils/input-dock-schedule-labels.js';

function isCountQuery(query)
{
	const qs = new URLSearchParams(String(query || '').replace(/^\?/, ''));
	return qs.get('group') === 'count';
}

function pageBounds(query)
{
	const qs = new URLSearchParams(String(query || '').replace(/^\?/, ''));
	const limit = Number(qs.get('limit') || 30);
	const offset = Number(qs.get('offset') || 0);
	return {
		limit: Number.isFinite(limit) ? limit : 30,
		offset: Number.isFinite(offset) ? offset : 0,
	};
}

function filtersToParams(filterData)
{
	const params = {};
	for (let i = 0; i < 16; i++)
	{
		const field = filterData?.[`field-${i}`];
		const value = filterData?.[`value-${i}`];
		if (!field || value == null || value === '')
			continue;
		params[field] = value;
	}
	return params;
}

function escHtml(s)
{
	return String(s ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function downloadBase64(b64, filename, mime)
{
	const bin = atob(b64);
	const bytes = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++)
		bytes[i] = bin.charCodeAt(i);
	const blob = new Blob([bytes], { type: mime });
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename;
	a.click();
	URL.revokeObjectURL(a.href);
}

function setText(root, sel, text)
{
	root?.querySelectorAll(sel)?.forEach((el) =>
	{
		el.textContent = text;
	});
}

const inputDockSchedules = function ()
{
	const rt_crud = crud.create({

		_lastSearchKey: null,
		_lastSearchRows: null,

		async loadBaseData()
		{
			const q = this.queryAll();
			const [companies, warehouses, suppliers, users, docks] = await Promise.all([
				api.call(api.list_companies, q),
				api.call(api.list_warehouses, q),
				api.call(api.list_suppliers, q),
				api.call(api.list_users, q),
				api.call(api.list_input_docks, q),
			]);
			await Promise.all([
				db.set('companies', companies || []).catch(() => {}),
				db.set('warehouses', warehouses || []).catch(() => {}),
				db.set('suppliers', suppliers || []).catch(() => {}),
				db.set('users', users || []).catch(() => {}),
				db.set('input_docks', docks || []).catch(() => {}),
			]);
		},

		async queryData(query)
		{
			const filterParams = filtersToParams(this.filter || {});
			let companies = filterParams.company_id
				? [{ id: await resolveCompanyId(filterParams.company_id) || filterParams.company_id }]
				: await db.list('companies').catch(() => []);
			if (!companies?.length)
				companies = await api.call(api.list_companies, '?limit=0') || [];

			companies = (companies || []).filter((c) =>
			{
				if (!c?.id) return false;
				if (filterParams.company_id && String(c.id) === String(filterParams.company_id))
					return true;
				const v = c.in_dock_scheduling_enabled;
				if (v === false || v === 0 || v === '0' || v === 'false')
					return false;
				// Sem campo no cache: ainda consulta (API decide / 400 flag off).
				return true;
			});

			if (filterParams.company_id && !companies.length)
			{
				const cid = await resolveCompanyId(filterParams.company_id) || filterParams.company_id;
				if (cid) companies = [{ id: cid }];
			}

			companies = (companies || []).filter((c) => c?.id);
			if (!companies.length)
				return isCountQuery(query) ? 0 : [];

			const docksCache = await db.list('input_docks').catch(() => []) || [];
			const dockByKey = new Map(docksCache.map((d) => [`${d.company_id}:${d.id}`, d]));

			let warehouseId = null;
			if (filterParams.warehouse_id)
			{
				const wh = await resolveWarehouseId(
					companies[0]?.id,
					filterParams.warehouse_id,
				);
				warehouseId = Number.isFinite(wh) && wh >= 1 ? wh : null;
			}

			let dockId = null;
			if (filterParams.dock_id != null && String(filterParams.dock_id).trim() !== '')
			{
				const rawDock = String(filterParams.dock_id).trim();
				if (/^\d+$/.test(rawDock))
					dockId = Number(rawDock);
				else
				{
					const found = docksCache.find((d) => d.name === rawDock);
					dockId = found?.id ?? null;
				}
			}

			let supplierId = null;
			if (filterParams.supplier_id != null && String(filterParams.supplier_id).trim() !== '')
			{
				const rawSup = String(filterParams.supplier_id).trim();
				if (/^\d+$/.test(rawSup))
					supplierId = Number(rawSup);
				else
				{
					const suppliers = await db.list('suppliers').catch(() => []) || [];
					const found = suppliers.find((s) =>
						s.nick === rawSup || s.name === rawSup
						|| String(s.id) === rawSup
					);
					supplierId = found?.id != null ? Number(found.id) : null;
				}
			}

			const searchKey = JSON.stringify({
				companies: companies.map((c) => c.id).sort(),
				date_from: filterParams.date_from || '',
				date_to: filterParams.date_to || '',
				warehouse_id: warehouseId || '',
				dock_id: dockId || '',
				input_order_id: filterParams.input_order_id || '',
				invoice_number: filterParams.invoice_number || '',
				supplier_id: supplierId || '',
				status: filterParams.status || '',
				has_occurrence: filterParams.has_occurrence || '',
			});

			let rows = this._lastSearchKey === searchKey && Array.isArray(this._lastSearchRows)
				? this._lastSearchRows
				: null;

			if (!rows)
			{
				const lists = await Promise.all(companies.map(async (company) =>
				{
					const q = new URLSearchParams();
					if (filterParams.date_from) q.set('date_from', String(filterParams.date_from));
					if (filterParams.date_to) q.set('date_to', String(filterParams.date_to));
					if (warehouseId) q.set('warehouse_id', String(warehouseId));
					if (dockId) q.set('dock_id', String(dockId));
					if (filterParams.input_order_id) q.set('input_order_id', String(filterParams.input_order_id));
					if (filterParams.invoice_number) q.set('invoice_number', String(filterParams.invoice_number));
					if (filterParams.supplier_id)
					{
						if (supplierId != null)
							q.set('supplier_id', String(supplierId));
					}
					if (filterParams.status) q.set('status', String(filterParams.status));
					if (filterParams.has_occurrence != null && filterParams.has_occurrence !== '')
					{
						const ho = filterParams.has_occurrence;
						const on = ho === true || ho === 1 || ho === '1' || ho === 'true' || ho === 'Sim';
						q.set('has_occurrence', on ? '1' : '0');
					}
					const qs = q.toString() ? `?${q.toString()}` : '';
					try
					{
						const data = await api.call(api.list_input_dock_schedules, company.id, qs);
						return (Array.isArray(data) ? data : []).map((row) =>
						{
							const dock = dockByKey.get(`${row.company_id}:${row.dock_id}`);
							return {
								...row,
								dock_name: dock?.name || null,
								dock_type: dock?.dock_type || null,
								dock_type_label: dock ? dockTypeLabel(dock.dock_type) : null,
							};
						});
					}
					catch (e)
					{
						console.warn('input-dock-schedules list', company.id, e);
						return [];
					}
				}));
				rows = lists.flat();
				rows.sort((a, b) => String(b.scheduled_start || '').localeCompare(String(a.scheduled_start || '')));
				this._lastSearchKey = searchKey;
				this._lastSearchRows = rows;
			}

			if (isCountQuery(query))
				return rows.length;
			const { limit, offset } = pageBounds(query);
			return rows.slice(offset, offset + limit);
		},

		async reloadPage()
		{
			this._lastSearchKey = null;
			this._lastSearchRows = null;
			return Object.getPrototypeOf(this).reloadPage.call(this);
		},

		async loadRecord(id)
		{
			const [company_id, schedule_id] = JSON.parse(id);
			const data = await api.call(api.select_input_dock_schedule, company_id, schedule_id);
			return data;
		},

		editRecord(data)
		{
			if (!data?.schedule)
				return;
			this.showDetail(data);
		},

		insertRecord()
		{
			this.openScheduleEditor({ mode: 'create' });
		},

		async _fetchSchedulesInRange(dateFrom, dateTo)
		{
			let companies = await db.list('companies').catch(() => []) || [];
			if (!companies.length)
				companies = await api.call(api.list_companies, '?limit=0') || [];
			companies = (companies || []).filter((c) =>
			{
				if (!c?.id) return false;
				const v = c.in_dock_scheduling_enabled;
				if (v === false || v === 0 || v === '0' || v === 'false')
					return false;
				return true;
			});
			if (!companies.length)
				companies = await api.call(api.list_companies, '?limit=0') || [];
			const docksCache = await db.list('input_docks').catch(() => []) || [];
			const dockByKey = new Map(docksCache.map((d) => [`${d.company_id}:${d.id}`, d]));
			const lists = await Promise.all((companies || []).filter((c) => c?.id).map(async (company) =>
			{
				const q = new URLSearchParams();
				if (dateFrom) q.set('date_from', dateFrom);
				if (dateTo) q.set('date_to', dateTo);
				try
				{
					const data = await api.call(api.list_input_dock_schedules, company.id, `?${q.toString()}`);
					return (Array.isArray(data) ? data : []).map((row) =>
					{
						const dock = dockByKey.get(`${row.company_id}:${row.dock_id}`);
						return {
							...row,
							dock_name: dock?.name || row.dock_name || null,
						};
					});
				}
				catch (e)
				{
					console.warn('calendar schedules', company.id, e);
					return [];
				}
			}));
			return lists.flat();
		},

		async loadPage(...args)
		{
			if (this._calendarOpen)
				return;
			return Object.getPrototypeOf(this).loadPage.apply(this, args);
		},

		openCalendar()
		{
			const work = this.target_node;
			const grid = work?.querySelector('#grid');
			if (!work || !grid)
				return notify('Área da grade não encontrada.', 'Calendário');
			this._calendarOpen = true;
			grid.style.display = 'none';
			let panel = work.querySelector('#ids-cal-panel');
			if (!panel)
			{
				panel = document.createElement('div');
				panel.id = 'ids-cal-panel';
				work.appendChild(panel);
			}
			panel.style.display = '';
			try
			{
			this._calendar = mountDockScheduleCalendar(panel, {
				fetchRange: (from, to) => this._fetchSchedulesInRange(from, to),
				onOpen: async (row) =>
				{
					try
					{
						const payload = await api.call(api.select_input_dock_schedule, row.company_id, row.id);
						if (!payload?.schedule)
							return notify('Agendamento não encontrado.', 'Calendário');
						showScheduleDetail({
							payload,
							onDone: () => this._calendar?.reload?.(),
						});
					}
					catch (e)
					{
						notify(e?.message || String(e), 'Calendário');
					}
				},
				onClose: () =>
				{
					this._calendar = null;
					this._calendarOpen = false;
					panel.remove();
					grid.style.display = '';
					void this.reloadPage();
				},
			});
			}
			catch (e)
			{
				this._calendar = null;
				this._calendarOpen = false;
				panel.remove();
				grid.style.display = '';
				notify(e?.message || String(e), 'Calendário');
			}
		},

		async openDetail()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (ids.length !== 1)
				return notify('Selecione exatamente um agendamento.', 'Agendamento de Docas');
			const [company_id, schedule_id] = ids[0];
			const data = await api.call(api.select_input_dock_schedule, company_id, schedule_id);
			if (!data?.schedule)
				return;
			this.showDetail(data);
		},

		showDetail(payload)
		{
			const schedule = payload.schedule;
			const companyId = schedule.company_id;
			const scheduleId = schedule.id;
			const fs = formset.create(detailForm, async (ev) =>
			{
				if (ev === 'Escape')
					fs.destroy();
			});
			fs.init();
			const root = document.querySelector('#input_dock_schedule_detail');
			if (!root) return;

			setText(root, '[data-sdp="id"]', String(schedule.id));
			setText(root, '[data-sdp="status"]', statusLabel(schedule.status));
			setText(root, '[data-sdp="title"]', `Agendamento #${schedule.id}`);

			const dock = payload.dock || {};
			const main = root.querySelector('#ids_detail_main');
			if (main)
			{
				main.innerHTML = [
					['Doca', dock.name || schedule.dock_id],
					['Tipo', dockTypeLabel(dock.dock_type)],
					['Armazém', schedule.warehouse_id],
					['Data', formatDate(schedule.scheduled_start)],
					['Horário', `${formatTime(schedule.scheduled_start)} – ${formatTime(schedule.scheduled_end)}`],
					['Duração', `${schedule.duration_minutes || '—'} min`],
					['Responsável', schedule.responsible_user_id || '—'],
					['Status', statusLabel(schedule.status)],
					['Ocorrência', schedule.has_occurrence ? 'Com ocorrência' : 'Sem ocorrência'],
				].map(([k, v]) => `<dt>${escHtml(k)}</dt><dd>${escHtml(v)}</dd>`).join('');
			}

			const orderBox = root.querySelector('#ids_detail_order');
			const order = payload.input_order || {};
			const supplier = payload.supplier || {};
			if (orderBox)
			{
				orderBox.innerHTML = [
					['Pedido Entrada', schedule.input_order_id],
					['NF', schedule.invoice_number || order.nf_number || '—'],
					['Fornecedor', supplier.nick || supplier.name || schedule.supplier_id || '—'],
					['Status pedido', order.status ?? '—'],
				].map(([k, v]) => `<dt>${escHtml(k)}</dt><dd>${escHtml(v)}</dd>`).join('');
			}
			const notesEl = root.querySelector('#ids_detail_notes');
			if (notesEl)
				notesEl.textContent = schedule.notes ? `Observações: ${schedule.notes}` : '';

			const evUl = root.querySelector('#ids_detail_events');
			const events = Array.isArray(payload.events) ? payload.events : [];
			if (evUl)
			{
				evUl.innerHTML = events.length
					? events.map((ev) => `
						<li>
							<div class="ids-tl-type">${escHtml(eventLabel(ev.event_type))}</div>
							<div class="ids-tl-when">${escHtml(ev.created_on || '')}</div>
						</li>`).join('')
					: '<li><div class="ids-tl-type">Sem eventos</div></li>';
			}

			const occs = Array.isArray(payload.occurrences) ? payload.occurrences : [];
			const occBlock = root.querySelector('#ids_detail_occ_block');
			const occUl = root.querySelector('#ids_detail_occs');
			if (occBlock && occUl)
			{
				if (occs.length)
				{
					occBlock.hidden = false;
					occUl.innerHTML = occs.map((o) => `
						<li>
							<div class="ids-tl-type">${escHtml(o.occurrence_type || o.type || 'Ocorrência')}</div>
							<div class="ids-tl-when">${escHtml(o.notes || o.created_on || '')}</div>
						</li>`).join('');
				}
				else
					occBlock.hidden = true;
			}

			const btnCancel = root.querySelector('#ids_detail_cancel');
			const btnReschedule = root.querySelector('#ids_detail_reschedule');
			const mutable = canMutateStatus(schedule.status);
			if (btnCancel) btnCancel.disabled = !mutable;
			if (btnReschedule) btnReschedule.disabled = !mutable;

			btnCancel?.addEventListener('click', () =>
			{
				if (!mutable)
					return notify('Agendamento não pode ser cancelado neste status.', 'Agendamento de Docas');
				fs.destroy();
				this.openCancel(companyId, scheduleId);
			});
			btnReschedule?.addEventListener('click', () =>
			{
				if (!mutable)
					return notify('Agendamento não pode ser reagendado neste status.', 'Agendamento de Docas');
				fs.destroy();
				this.openScheduleEditor({
					mode: 'reschedule',
					companyId,
					scheduleId,
					schedule,
					dock,
				});
			});
		},

		async openCancelFromList()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (ids.length !== 1)
				return notify('Selecione exatamente um agendamento.', 'Agendamento de Docas');
			const [company_id, schedule_id] = ids[0];
			const cached = (this._lastSearchRows || []).find((r) =>
				String(r.company_id) === String(company_id) && Number(r.id) === Number(schedule_id)
			);
			if (cached && !canMutateStatus(cached.status))
				return notify('Agendamento não pode ser cancelado neste status.', 'Agendamento de Docas');
			this.openCancel(company_id, schedule_id);
		},

		openCancel(companyId, scheduleId)
		{
			const fs = formset.create(cancelForm, async (data) =>
			{
				const reason = String(data.cancel_reason || '').trim();
				if (!reason)
					return notify('Informe o motivo do cancelamento.', 'Agendamento de Docas');
				ask('Confirma o cancelamento deste agendamento?', 'Cancelar', async (answer) =>
				{
					if (answer !== 'Y') return;
					const resp = await api.call(
						api.cancel_input_dock_schedule,
						companyId,
						scheduleId,
						{ cancel_reason: reason },
					);
					if (!resp) return;
					notify('Agendamento cancelado.', 'Sucesso');
					fs.destroy();
					await this.reloadPage();
				});
			});
			fs.init();
		},

		async openRescheduleFromList()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (ids.length !== 1)
				return notify('Selecione exatamente um agendamento.', 'Agendamento de Docas');
			const [company_id, schedule_id] = ids[0];
			const data = await api.call(api.select_input_dock_schedule, company_id, schedule_id);
			if (!data?.schedule) return;
			if (!canMutateStatus(data.schedule.status))
				return notify('Agendamento não pode ser reagendado neste status.', 'Agendamento de Docas');
			this.openScheduleEditor({
				mode: 'reschedule',
				companyId: company_id,
				scheduleId: schedule_id,
				schedule: data.schedule,
				dock: data.dock,
			});
		},

		openScheduleEditor({ mode, companyId, scheduleId, schedule, dock } = {})
		{
			openSharedScheduleEditor({
				mode,
				companyId,
				scheduleId,
				schedule,
				dock,
				onDone: () => this.reloadPage(),
			});
		},

		async _reportPairs()
		{
			const selected = await this.getSelectedIds(['company_id', 'id']);
			if (selected.length)
				return selected;
			const rows = Array.isArray(this._lastSearchRows) ? this._lastSearchRows : [];
			if (!rows.length)
			{
				notify('Selecione as linhas ou filtre a lista antes de imprimir.', 'Agendamento de Docas');
				return null;
			}
			return rows.map((r) => [r.company_id, r.id]);
		},

		_groupPairs(pairs)
		{
			const byCompany = new Map();
			for (const [company_id, id] of pairs)
			{
				if (!byCompany.has(company_id))
					byCompany.set(company_id, []);
				byCompany.get(company_id).push(Number(id));
			}
			return byCompany;
		},

		printControlSheet()
		{
			void (async () =>
			{
				const pairs = await this._reportPairs();
				if (!pairs)
					return;
				try
				{
					for (const [company_id, schedule_ids] of this._groupPairs(pairs))
					{
						const resp = await api.call(api.print_input_dock_schedule_control, company_id, {
							schedule_ids,
						});
						if (resp?.pdf)
							pdf(resp.pdf);
						else
							notify('Nenhum PDF retornado.', 'Controle de Agendamento');
					}
				}
				catch (e)
				{
					notify(e?.message || String(e), 'Controle de Agendamento');
				}
			})();
		},

		exportExcel()
		{
			void (async () =>
			{
				const pairs = await this._reportPairs();
				if (!pairs)
					return;
				try
				{
					for (const [company_id, schedule_ids] of this._groupPairs(pairs))
					{
						const resp = await api.call(api.export_input_dock_schedules_xlsx, company_id, {
							schedule_ids,
						});
						if (resp?.xlsx)
						{
							downloadBase64(
								resp.xlsx,
								resp.filename || 'agendamentos-docas.xlsx',
								'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
							);
						}
						else
							notify('Nenhuma planilha retornada.', 'Extrato Excel');
					}
				}
				catch (e)
				{
					notify(e?.message || String(e), 'Extrato Excel');
				}
			})();
		},
	});

	router.set('#input-dock-schedule-calendar', () => rt_crud.openCalendar());
	router.set('#input-dock-schedule-detail', () => rt_crud.openDetail());
	router.set('#input-dock-schedule-cancel', () => rt_crud.openCancelFromList());
	router.set('#input-dock-schedule-reschedule', () => rt_crud.openRescheduleFromList());
	router.set('#input-dock-schedule-print-control', () => rt_crud.printControlSheet());
	router.set('#input-dock-schedule-export-xlsx', () => rt_crud.exportExcel());

	const fieldspecs = [
		{ index: 1, field: 'date_from', label: 'Período inicial', types: [types.date()], placeholder: '', title: 'Data/hora inicial (scheduled_end > date_from)' },
		{ index: 2, field: 'date_to', label: 'Período final', types: [types.date()], placeholder: '', title: 'Data/hora final (scheduled_start < date_to)' },
		{ index: 3, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 4, field: 'warehouse_id', label: 'Armazém', types: [types.datalist()], placeholder: '', title: 'Armazém', store: 'warehouses', labels: ['name'] },
		{ index: 5, field: 'dock_id', label: 'Doca', types: [types.datalist()], placeholder: '', title: 'Doca (nome ou id)', store: 'input_docks', labels: ['name'] },
		{ index: 6, field: 'status', label: 'Status', types: [opt_input_dock_schedule_status(), types.datalist()], placeholder: '', title: 'Status do agendamento' },
		{ index: 7, field: 'input_order_id', label: 'Pedido Entrada', types: [], placeholder: '', title: 'ID do Pedido de Entrada' },
		{ index: 8, field: 'invoice_number', label: 'NF', types: [], placeholder: '', title: 'Número da NF' },
		{ index: 9, field: 'supplier_id', label: 'Fornecedor', types: [types.datalist()], placeholder: '', title: 'Fornecedor', store: 'suppliers', labels: ['nick'] },
		{ index: 10, field: 'has_occurrence', label: 'Com ocorrência', types: [types.opt_bool(), types.datalist()], placeholder: '', title: 'Filtrar com ocorrência' },
		{ index: 11, field: 'dock_type', label: 'Tipo', types: [opt_input_dock_type(), types.datalist()], placeholder: '', title: 'Tipo (só visual/filtro local se usado)', computed: true },
	];

	const operations = [
		{ hash: '#input-dock-schedule-calendar', label: 'Calendário' },
		{ hash: '#input-dock-schedule-detail', label: 'Detalhar' },
		{ hash: '#input-dock-schedule-cancel', label: 'Cancelar' },
		{ hash: '#input-dock-schedule-reschedule', label: 'Reagendar' },
	];

	const reports = [
		{ hash: '#input-dock-schedule-print-control', label: 'Controle&nbsp;de&nbsp;Agendamento' },
		{ hash: '#input-dock-schedule-export-xlsx', label: 'Extrato&nbsp;Excel' },
	];

	rt_crud.run(
		'input_dock_schedules',
		'Agendamento&nbsp;de&nbsp;Docas',
		document.getElementById('workarea'),
		view,
		null,
		fieldspecs,
		operations,
		reports
	);
};

export default inputDockSchedules;
